import { useMemo, useState } from 'react';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@ludolio/shared-ui';
import { Check, Eye, Search, X } from 'lucide-react';
import type { UnitStudent } from './types';

type ActivityResponse = {
  id: string;
  studentId: string;
  submittedAt: string;
  response?: { answers?: Array<{ answer: string }> | null } | null;
};

type QuestionConfig = {
  question: string;
  options?: string[] | null;
  correctAnswers?: string[] | null;
};

type ActivityConfig = {
  questions?: QuestionConfig[] | null;
};

type ActivityItem = {
  id: string;
  title: string;
  type: 'quiz' | 'short_answer';
  config?: ActivityConfig | null;
  game?: { title?: string | null } | null;
  responses?: ActivityResponse[];
};

interface ActivityMarkbookTabProps {
  students: UnitStudent[];
  activities: ActivityItem[];
}

type StatusFilter = 'all' | 'submitted' | 'missing';
type ActivityTypeFilter = 'all' | 'quiz' | 'short_answer';

type AnswerDialogState = {
  studentName: string;
  studentEmail: string;
  activityTitle: string;
  gameTitle: string;
  type: 'quiz' | 'short_answer';
  questions: QuestionConfig[];
  answers: Array<{ answer: string }>;
  submittedAt: string;
};

const getStudentName = (student: UnitStudent) => {
  const fullName = [student.firstName, student.lastName].filter(Boolean).join(' ').trim();
  return fullName || student.email;
};

export function ActivityMarkbookTab({ students, activities }: ActivityMarkbookTabProps) {
  const [studentSearch, setStudentSearch] = useState('');
  const [activitySearch, setActivitySearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [activityTypeFilter, setActivityTypeFilter] = useState<ActivityTypeFilter>('all');
  const [selectedAnswer, setSelectedAnswer] = useState<AnswerDialogState | null>(null);

  const enrolledStudents = useMemo(
    () => [...students].filter((student) => student.status === 'enrolled').sort((a, b) => getStudentName(a).localeCompare(getStudentName(b))),
    [students]
  );

  const latestResponses = useMemo(() => {
    const responseMap = new Map<string, ActivityResponse>();

    activities.forEach((activity) => {
      activity.responses?.forEach((response) => {
        const key = `${response.studentId}:${activity.id}`;
        const current = responseMap.get(key);

        if (!current || new Date(response.submittedAt).getTime() > new Date(current.submittedAt).getTime()) {
          responseMap.set(key, response);
        }
      });
    });

    return responseMap;
  }, [activities]);

  const visibleActivities = useMemo(() => {
    const query = activitySearch.trim().toLowerCase();

    return activities.filter((activity) => {
      const matchesSearch = !query
        || activity.title.toLowerCase().includes(query)
        || (activity.game?.title || '').toLowerCase().includes(query);
      const matchesType = activityTypeFilter === 'all' || activity.type === activityTypeFilter;

      return matchesSearch && matchesType;
    });
  }, [activities, activitySearch, activityTypeFilter]);

  const filteredStudents = useMemo(() => {
    const query = studentSearch.trim().toLowerCase();

    return enrolledStudents.filter((student) => {
      const fullName = getStudentName(student).toLowerCase();
      const matchesStudent = !query || fullName.includes(query) || student.email.toLowerCase().includes(query);

      if (!matchesStudent) return false;
      if (statusFilter === 'all' || visibleActivities.length === 0) return true;

      const submissionCount = visibleActivities.filter((activity) => latestResponses.get(`${student.id}:${activity.id}`)).length;

      if (statusFilter === 'submitted') {
        return submissionCount > 0;
      }

      return submissionCount < visibleActivities.length;
    });
  }, [enrolledStudents, latestResponses, statusFilter, studentSearch, visibleActivities]);

  if (activities.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          Create activities first to populate the markbook.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h3 className="text-lg font-semibold">Markbook</h3>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline">Students: {filteredStudents.length}</Badge>
          <Badge variant="outline">Activities: {visibleActivities.length}</Badge>
          <Badge variant="outline">Enrolled: {enrolledStudents.length}</Badge>
        </div>
      </div>

      <div className="grid gap-3 lg:grid-cols-[minmax(220px,1fr)_minmax(220px,1fr)_170px_180px]">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={studentSearch} onChange={(event) => setStudentSearch(event.target.value)} placeholder="Search students" aria-label="Search students" className="pl-9" />
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={activitySearch} onChange={(event) => setActivitySearch(event.target.value)} placeholder="Search activities" aria-label="Search activities" className="pl-9" />
        </div>
        <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as StatusFilter)}>
          <SelectTrigger aria-label="Filter by submission status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All status</SelectItem>
            <SelectItem value="submitted">Has submission</SelectItem>
            <SelectItem value="missing">Has missing</SelectItem>
          </SelectContent>
        </Select>
        <Select value={activityTypeFilter} onValueChange={(value) => setActivityTypeFilter(value as ActivityTypeFilter)}>
          <SelectTrigger aria-label="Filter by activity type">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="quiz">Quiz</SelectItem>
            <SelectItem value="short_answer">Short answer</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle>Activity responses</CardTitle>
        </CardHeader>
        <CardContent>
          {visibleActivities.length === 0 ? (
            <div className="flex h-[520px] items-center justify-center rounded-md border text-sm text-muted-foreground">
              No activities match the current filters.
            </div>
          ) : (
            <div className="h-[520px] overflow-auto rounded-md border">
              <table className="w-full min-w-max caption-bottom text-sm">
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="sticky left-0 top-0 z-30 min-w-[240px] bg-white px-4 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)] dark:bg-[hsl(222.2,84%,4.9%)]">Student</TableHead>
                    {visibleActivities.map((activity) => (
                      <TableHead key={activity.id} className="sticky top-0 z-20 min-w-[170px] max-w-[170px] bg-background align-top">
                        <div className="space-y-1 py-2">
                          <p className="truncate text-sm font-medium text-foreground">{activity.title}</p>
                          <p className="truncate text-[11px] text-muted-foreground">{activity.game?.title || 'Activity'} · {activity.type === 'quiz' ? 'Quiz' : 'Short answer'}</p>
                        </div>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredStudents.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={visibleActivities.length + 1} className="h-24 text-center text-muted-foreground">No enrolled students match the current filters.</TableCell>
                    </TableRow>
                  ) : (
                    filteredStudents.map((student) => (
                      <TableRow key={student.id}>
                        <th scope="row" className="sticky left-0 z-10 min-w-[240px] bg-white p-2 px-4 text-left align-top font-normal shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)] dark:bg-[hsl(222.2,84%,4.9%)]">
                          <div className="space-y-1">
                            <p className="font-medium leading-tight">{getStudentName(student)}</p>
                            <p className="truncate text-[11px] text-muted-foreground">{student.email}</p>
                          </div>
                        </th>
                        {visibleActivities.map((activity) => {
                          const response = latestResponses.get(`${student.id}:${activity.id}`);
                          const studentAnswers = response?.response?.answers ?? [];
                          const questions = activity.config?.questions ?? [];
                          // Compute per-question correctness; null = ungraded
                          const gradedResults = questions.map((q, i) => {
                            if (!q.correctAnswers || q.correctAnswers.length === 0) return null;
                            const ans = studentAnswers[i]?.answer?.trim() ?? '';
                            if (!ans) return false;
                            if (q.correctAnswers.length > 1) {
                              try {
                                const picks: string[] = JSON.parse(ans);
                                return Array.isArray(picks) && [...picks].sort().join('|') === [...q.correctAnswers].sort().join('|');
                              } catch { return false; }
                            }
                            return q.correctAnswers.includes(ans);
                          }).filter((r): r is boolean => r !== null);
                          const isCorrect: boolean | null = gradedResults.length === 0 ? null : gradedResults.every(Boolean);
                          const displayAnswer = studentAnswers[0]?.answer?.trim() ?? '';
                          const statusLabel = !response
                            ? 'Missing'
                            : isCorrect === true
                              ? 'Correct'
                              : isCorrect === false
                                ? 'Incorrect'
                                : 'Submitted';

                          let cellBg: string;
                          if (!response) {
                            cellBg = 'bg-rose-50/60 dark:bg-rose-950/20';
                          } else if (isCorrect === true) {
                            cellBg = 'bg-emerald-50/80 dark:bg-emerald-950/20';
                          } else if (isCorrect === false) {
                            cellBg = 'bg-amber-50/80 dark:bg-amber-950/20';
                          } else {
                            cellBg = 'bg-emerald-50/80 dark:bg-emerald-950/20';
                          }

                          return (
                            <TableCell key={activity.id} className={`min-w-[170px] max-w-[170px] px-2 py-1.5 ${cellBg}`}>
                              <div className="flex items-center gap-1.5">
                                {isCorrect === true && (
                                  <span title="Correct" className="flex-shrink-0" aria-hidden="true">
                                    <Check className="h-3.5 w-3.5 text-emerald-600" />
                                  </span>
                                )}
                                {isCorrect === false && (
                                  <span title="Incorrect" className="flex-shrink-0" aria-hidden="true">
                                    <X className="h-3.5 w-3.5 text-rose-500" />
                                  </span>
                                )}
                                {isCorrect === null && (
                                  <span
                                    className={`h-2 w-2 flex-shrink-0 rounded-full ${response ? 'bg-emerald-500' : 'bg-rose-400'}`}
                                    title={response ? 'Submitted' : 'Missing'}
                                    aria-hidden="true"
                                  />
                                )}
                                <span className="sr-only">{statusLabel}. </span>
                                <span className="min-w-0 flex-1 truncate text-xs text-foreground">
                                  {displayAnswer || ''}
                                </span>
                                {response ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-5 flex-shrink-0 px-1.5 text-[10px]"
                                    aria-label={`View ${activity.title} response from ${getStudentName(student)}`}
                                    title="View response"
                                    onClick={() => setSelectedAnswer({
                                      studentName: getStudentName(student),
                                      studentEmail: student.email,
                                      activityTitle: activity.title,
                                      gameTitle: activity.game?.title || 'Activity',
                                      type: activity.type,
                                      questions,
                                      answers: studentAnswers,
                                      submittedAt: response.submittedAt,
                                    })}
                                  >
                                    <Eye className="h-3 w-3" aria-hidden="true" />
                                  </Button>
                                ) : null}
                              </div>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!selectedAnswer} onOpenChange={(open) => !open && setSelectedAnswer(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedAnswer?.activityTitle}</DialogTitle>
            <DialogDescription>
              {selectedAnswer?.studentName} · {selectedAnswer?.gameTitle} · {selectedAnswer?.type === 'quiz' ? 'Quiz' : 'Short answer'}
            </DialogDescription>
          </DialogHeader>
          {selectedAnswer ? (
            <div className="space-y-4">
              <div className="space-y-1 text-sm">
                <p className="font-medium">{selectedAnswer.studentName}</p>
                <p className="text-muted-foreground">{selectedAnswer.studentEmail}</p>
                <p className="text-xs text-muted-foreground">Submitted {new Date(selectedAnswer.submittedAt).toLocaleString()}</p>
              </div>
              <div className="space-y-3">
                {selectedAnswer.questions.map((q, i) => {
                  const studentAns = selectedAnswer.answers[i]?.answer?.trim() ?? '';
                  const correctAnswers = q.correctAnswers;
                  const hasCorrect = correctAnswers && correctAnswers.length > 0;
                  const isCorrect: boolean | null = hasCorrect && studentAns
                    ? (() => {
                        if (correctAnswers!.length > 1) {
                          try {
                            const picks: string[] = JSON.parse(studentAns);
                            return Array.isArray(picks) && [...picks].sort().join('|') === [...correctAnswers!].sort().join('|');
                          } catch { return false; }
                        }
                        return correctAnswers!.includes(studentAns);
                      })()
                    : null;
                  const displayAnswer = (() => {
                    if (!studentAns) return '—';
                    try {
                      const parsed: string[] = JSON.parse(studentAns);
                      return Array.isArray(parsed) ? parsed.join(', ') : studentAns;
                    } catch { return studentAns; }
                  })();

                  return (
                    <div key={i} className="space-y-1">
                      {selectedAnswer.questions.length > 1 && (
                        <p className="text-xs font-semibold text-muted-foreground">Question {i + 1}: {q.question}</p>
                      )}
                      <div className={`rounded-md border p-3 ${isCorrect === true ? 'border-emerald-300 bg-emerald-50/50 dark:border-emerald-700 dark:bg-emerald-950/30' : isCorrect === false ? 'border-rose-300 bg-rose-50/50 dark:border-rose-700 dark:bg-rose-950/30' : 'bg-muted/30'}`}>
                        <div className="flex items-start gap-2">
                          {isCorrect === true && <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />}
                          {isCorrect === false && <X className="mt-0.5 h-4 w-4 flex-shrink-0 text-rose-500" />}
                          <p className="whitespace-pre-wrap break-words text-sm leading-6">{displayAnswer}</p>
                        </div>
                        {isCorrect === true && <p className="mt-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">Correct</p>}
                        {isCorrect === false && <p className="mt-1 text-xs font-medium text-rose-500 dark:text-rose-400">Incorrect</p>}
                      </div>
                      {isCorrect === false && hasCorrect && (
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {correctAnswers!.map((ca, ci) => (
                            <Badge key={ci} variant="success" className="flex items-center gap-1">
                              <Check className="h-3 w-3" />{ca}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}